import React from 'react';
import { 
    ShieldCheck, 
    ClipboardCheck, 
    AlertCircle, 
    RefreshCw, 
    CreditCard,
    Phone,
    Mail,
    CheckCircle2,
    Clock,
    Info,
    HelpCircle,
    ArrowRight,
    X,
    CheckCircle,
    Edit3,
    AlertOctagon
} from 'lucide-react';
import { RefundPolicyMetaData } from '../layouts/MetaData/RefundPolicyMetaData';

const RefundPolicy = () => {
    const refundSteps = [
        {
            icon: <AlertCircle className="w-5 h-5 text-red-600" />,
            title: "Contact Support",
            text: "Reach out to our customer support within 24 hours of delivery"
        },
        {
            icon: <ClipboardCheck className="w-5 h-5 text-red-600" />,
            title: "Provide Details",
            text: "Share your order number and provide proof if applicable (photos/videos for quality issues)"
        },
        {
            icon: <ShieldCheck className="w-5 h-5 text-red-600" />,
            title: "Verification",
            text: "Our team will review and approve/refuse based on policy guidelines"
        },
        {
            icon: <CreditCard className="w-5 h-5 text-red-600" />,
            title: "Refund Processing",
            text: "Once approved, refund will be initiated based on your payment method"
        }
    ];

    const refundExceptions = [
        "If the customer provides an incorrect or incomplete delivery address",
        "If the recipient is unavailable at the time of delivery, leading to order failure",
        "If a customer refuses to accept the order without a valid reason",
        "If the refund request is made beyond the 24-hour window after delivery",
        "If the product is tampered with or used after delivery"
    ];

    return (
        <>
            <RefundPolicyMetaData />
            <div 
                className="pt-0"
                style={{
                    backgroundImage: `url(https://meatbuddy-assets.s3.eu-north-1.amazonaws.com/other-images/meatbuddy-image-web-banner-background-5.png)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: 'auto',
                    backgroundPosition: 'center'
                }}
            >
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {/* Header Section */}
                    <div className="text-center mb-10">
                        <h1 className="text-2xl md:text-3xl font-bold text-red-600 mb-3">
                            Refund Policy
                        </h1>
                        <div className="text-base text-gray-600 max-w-3xl mx-auto">
                            <p>
                                At Meatbuddy, customer satisfaction is our top priority. We strive to provide high-quality, fresh meat products, and we stand behind the integrity of our services. However, if you are unsatisfied with your purchase, we offer a hassle-free refund policy as outlined below.
                            </p>
                        </div>
                    </div>

                    <div className="space-y-6 max-w-4xl mx-auto">
                        {/* General Refund Conditions */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Info className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">General Refund Conditions</h2>
                            </div>
                            <div className="space-y-3">
                                <ul className="space-y-2">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">Refunds are applicable <strong>only for quality-related issues, incorrect orders, or failed transactions</strong></span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">Requests for refunds must be made within <strong>24 hours of delivery</strong> by contacting our customer support team</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">Refunds will not be processed for <strong>change of mind</strong> or <strong>customer preference</strong> once an order has been delivered</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">If a product is found to be damaged, spoiled, or not as described in terms of freshness and quality, we have a <strong>"No Questions Asked"</strong> policy, and we will initiate the refund or replacement according to the customers' preference</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">Refunds will be processed only to the <strong>original payment method</strong> or as <strong>store credit</strong>, as per the customer's choice and policy eligibility</span>
                                    </li>
                                </ul>
                            </div>
                        </section>

                        {/* Important Notice */}
                        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <AlertCircle className="h-5 w-5 text-yellow-400" />
                                </div>
                                <div className="ml-3">
                                    <p className="text-xs text-yellow-700">
                                        For quality-related concerns, please report within 24 hours of delivery for prompt assistance.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Online Payment Section */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <CreditCard className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Online Payment Refunds</h2>
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-start space-x-3">
                                    <Clock className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div className="space-y-2">
                                        <p className="text-gray-600 text-xs leading-relaxed">
                                            Refunds for online payments (Credit/Debit Card, UPI, Net Banking, Wallets) will be initiated within 48 hours after approval and credited to the customer's account within 5-7 working days, depending on the policies of the issuing bank or payment gateway.
                                        </p>
                                        <ul className="space-y-2 pl-4">
                                            <li className="flex items-center gap-2">
                                                <ArrowRight className="w-4 h-4 text-red-600" />
                                                <span className="text-gray-600 text-xs">Processing time: Within 48 hours</span>
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <ArrowRight className="w-4 h-4 text-red-600" />
                                                <span className="text-gray-600 text-xs">Credit to account: 5-7 working days</span>
                                            </li>
                                            <li className="flex items-center gap-2">
                                                <ArrowRight className="w-4 h-4 text-red-600" />
                                                <span className="text-gray-600 text-xs">A confirmation email/SMS will be sent once the refund has been processed</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* COD Section */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <ShieldCheck className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Cash on Delivery (COD) Refunds</h2>
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-start space-x-3">
                                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div className="space-y-2">
                                        <p className="text-gray-600 text-xs leading-relaxed">
                                            For COD orders, refunds will be provided as store credit, which can be used for future purchases on Meatbuddy. The store credit will be issued within 24-48 hours after the refund request is approved.
                                        </p>
                                        <div className="bg-gray-50 p-3 rounded-lg">
                                            <p className="text-xs font-medium text-gray-700">
                                                Store credits do not expire and can be redeemed anytime on the platform
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Failed Transactions Section */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <AlertOctagon className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Failed Transactions & Double Charges</h2>
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-start space-x-3">
                                    <Clock className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div className="space-y-2">
                                        <ul className="space-y-2">
                                            <li className="flex items-start gap-2">
                                                <ArrowRight className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                                <span className="text-gray-600 text-xs">In case of a failed transaction where money is deducted but the order is not placed, a refund will be automatically initiated within 24-48 hours and should reflect in your account within 5-7 working days</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <ArrowRight className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                                <span className="text-gray-600 text-xs">If a duplicate payment is made due to a technical error, the excess amount will be refunded upon verification</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Order Cancellations Section */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <Edit3 className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Order Cancellations & Modifications</h2>
                            </div>
                            <div className="space-y-3">
                                <ul className="space-y-2">
                                    <li className="flex items-start gap-2">
                                        <ArrowRight className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">Orders can be cancelled within 1 hour of placement to qualify for a full refund</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ArrowRight className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">If the order is already processed or dispatched, cancellation may not be possible</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ArrowRight className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">Modifications to an order (e.g., change of delivery address, product substitution) must be requested before processing of the order</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <ArrowRight className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">If an order is cancelled by Meatbuddy due to unforeseen circumstances (e.g., unavailability of products), a full refund will be issued</span>
                                    </li>
                                </ul>
                            </div>
                        </section>

                        {/* Refund Exceptions */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <X className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Refund Exceptions</h2>
                            </div>
                            <p className="text-gray-600 text-xs leading-relaxed mb-3">
                                Refunds will <strong>not</strong> be provided in the following cases:
                            </p>
                            <div className="space-y-2">
                                {refundExceptions.map((exception, index) => (
                                    <div key={index} className="flex items-start gap-2 bg-gray-50 p-3 rounded-lg">
                                        <X className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                                        <span className="text-gray-600 text-xs">{exception}</span>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Refund Process */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <RefreshCw className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">How to Request a Refund</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {refundSteps.map((step, index) => (
                                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0">
                                                {step.icon}
                                            </div>
                                            <div>
                                                <h3 className="font-semibold text-gray-900 text-sm mb-1">{step.title}</h3>
                                                <p className="text-xs text-gray-600 leading-relaxed">{step.text}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {/* Contact Information */}
                        <section className="bg-white rounded-lg p-6 shadow-md">
                            <div className="flex items-center mb-3">
                                <HelpCircle className="w-6 h-6 text-red-600 mr-3" />
                                <h2 className="text-lg font-semibold text-red-600">Need Help?</h2>
                            </div>
                            <p className="text-gray-600 text-xs leading-relaxed mb-4">
                                For any refund related queries, our support team is here to help:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <a 
                                    href="mailto:customersupport@meatbuddy.in"
                                    className="flex items-center space-x-3 bg-gray-50 p-4 rounded-lg hover:bg-red-50 transition-colors"
                                >
                                    <Mail className="w-5 h-5 text-red-600" />
                                    <span className="text-gray-700 text-sm">customersupport@meatbuddy.in</span>
                                </a>
                                <a 
                                    href="tel:+916364749404"
                                    className="flex items-center space-x-3 bg-gray-50 p-4 rounded-lg hover:bg-red-50 transition-colors"
                                >
                                    <Phone className="w-5 h-5 text-red-600" />
                                    <span className="text-gray-700 text-sm">+91- 6364749404</span>
                                </a>
                            </div>
                        </section>

                        {/* Policy Changes Notice */}
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <p className="text-xs text-gray-600">
                                Meatbuddy reserves the right to modify or update this Refund Policy at any time. Customers are encouraged to review this policy periodically for any changes.
                            </p>
                        </div>

                        {/* Call to Action */}
                        <div className="text-center mt-8">
                            <p className="text-base text-gray-600 mb-5">
                                If you have any further questions or concerns regarding refunds, feel free to reach out to our Customer Support Team—we're here to help!
                            </p>
                            <a 
                                href="/faq" 
                                className="inline-block bg-red-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-red-700 transition-colors text-sm"
                            >
                                View FAQs
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RefundPolicy;